import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import styled from 'styled-components';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { HelpBox } from 'components/v2/help-boxes';
import { toaster } from 'components/v2/toast';
import { useVerifierMarketplaceCountriesLookupWithGlobal } from 'hooks/lookups';
import { Option } from 'types/types';
import { AddPractitionerToCompanyModel } from 'types/dynamic-forms/companies/AddPractitionerToCompanyModel';
import { LcaPractitionerCompanyModel } from 'types/dynamic-forms/companies/LcaPractitionerCompanyModel';
import LcaCompaniesList from 'components/v2/compiler/panels/lca-support/lca-practitioner-selector/LcaCompaniesList';
import InviteExternalPractitionerTextInput from 'components/v2/compiler/panels/lca-support/lca-practitioner-selector/InviteExternalPractitionerTextInput';
import { COLLABORATION_REQUEST_SENT_TEXT } from 'components/v2/compiler/constants';
import { getLcaPractitionerCompanies, postRequestLcaPractitionerCollaboration } from 'services/api/dynamicFormsCompaniesApi';
import useDialogListQuery, { dialogListQueryOptions } from '../../hooks/useDialogListQuery';

type CompanyStatus = { successOperationText?: string; errorText?: string };

type TProps = {
  onHide(): void;
  isOpened: boolean;
  /** Whether the dialog can be opened at all, so its list is worth loading before it opens. */
  canPrefetch: boolean;
  companyId: string;
  selectedPractitioners?: Option[] | undefined;
  companyPractitioners?: Option[] | undefined;
  onAddPractitioner(model: AddPractitionerToCompanyModel): Promise<void>;
};

const lcaPractitionerCompaniesQueryOptions = (country: Option | undefined) =>
  dialogListQueryOptions(['DF_LCA_PRACTITIONER_COMPANIES', country?.value ?? null], () =>
    getLcaPractitionerCompanies(country?.value)
  );

const LcaPractitionerSelectInputRendererDialog = ({
  onHide,
  isOpened,
  canPrefetch,
  companyId,
  selectedPractitioners,
  companyPractitioners,
  onAddPractitioner,
}: TProps) => {
  const queryClient = useQueryClient();
  const [selectedCountry, setSelectedCountry] = useState<Option | undefined>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Request results are kept apart from the cached list, so a search or a reload does not lose them.
  const [companyStatuses, setCompanyStatuses] = useState<Record<string, CompanyStatus>>({});
  const [selectedLcaPractitionerCompany, setSelectedLcaPractitionerCompany] = useState<LcaPractitionerCompanyModel | undefined>();

  const [areButtonsDisabled, setAreButtonsDisabled] = useState<boolean>(false);

  // The unfiltered list the dialog opens with is loaded as soon as the form is on screen, so it is usually ready by then.
  useEffect(() => {
    if (canPrefetch) {
      void queryClient.prefetchQuery(lcaPractitionerCompaniesQueryOptions(undefined));
    }
  }, [canPrefetch, queryClient]);

  // Not prefetched: other screens share this cache entry with another shape of query.
  const countries = useVerifierMarketplaceCountriesLookupWithGlobal({ enabled: isOpened }).data || [];

  const companiesQuery = useDialogListQuery(lcaPractitionerCompaniesQueryOptions(selectedCountry), isOpened);

  const lcaPractitionerCompanies = useMemo(
    () => [...(companiesQuery.data ?? [])].sort((a, b) => ((a.name ?? '') > (b.name ?? '') ? 1 : -1)),
    [companiesQuery.data]
  );

  const filteredLcaPractitionerCompanies = useMemo(() => {
    const matching = searchTerm
      ? lcaPractitionerCompanies.filter(
          (item) =>
            item.name?.toLowerCase().includes(searchTerm) ||
            item.members?.some((m) => m.email?.toLowerCase().includes(searchTerm))
        )
      : lcaPractitionerCompanies;
    return matching.map((c) => (companyStatuses[c.id] ? { ...c, ...companyStatuses[c.id] } : c));
  }, [lcaPractitionerCompanies, searchTerm, companyStatuses]);

  const setCompanyStatus = (targetCompanyId: string, status: CompanyStatus) =>
    setCompanyStatuses((prev) => ({ ...prev, [targetCompanyId]: status }));

  const onChangeLcaPractitionerCountry = async (_: string, val: any) => {
    if (!countries.length) return;
    setSelectedCountry(countries.find((c) => c.value === val));
  };

  const handleAddLcaPractitioner = async (membershipId: string) => {
    setAreButtonsDisabled(true);

    try {

      const connectPractitionerModel: AddPractitionerToCompanyModel = {
        companyId: companyId,
        membershipId: membershipId,
      };
      await onAddPractitioner(connectPractitionerModel);
      toaster({
        severity: 'success',
        summary: 'The LCA Practitioner was added to the EPD!',
        details: 'Now the EPD Practitioner has an access to the EPD.',
      });
      onHide();
    } catch {
    } finally {
      setAreButtonsDisabled(false);
    }
  };

  const handleRequestCollaboration = async (targetCompanyId: string) => {
    setAreButtonsDisabled(true);

    try {
      await postRequestLcaPractitionerCollaboration(companyId, targetCompanyId);
      setCompanyStatus(targetCompanyId, { successOperationText: COLLABORATION_REQUEST_SENT_TEXT });
      toaster({
        severity: 'success',
        summary: 'Your collaboration request has been sent.',
        details: 'Please wait for a confirmation response.',
      });
    } catch {
      setCompanyStatus(targetCompanyId, { errorText: 'Something went wrong.' });
    } finally {
      setAreButtonsDisabled(false);
    }
  };

  const handleSearchLcaPractitionerCompanies = (event: any) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  return (
    <StyledDialog
      resizable={false}
      header="LCA Practitioner"
      footer={
        <DialogFooter>
          <HelpBox>
            Can't find your LCA Practitioner in the list? Enter their email below to send an invitation to the EPD Portal.
            Once they register, you will be able to share access to your EPD.
          </HelpBox>
          <InviteExternalPractitionerWrapper>
            <InviteExternalPractitionerTextInput companyId={companyId} />
          </InviteExternalPractitionerWrapper>
        </DialogFooter>
      }
      visible={isOpened}
      contentStyle={!!filteredLcaPractitionerCompanies.length ? {} : { overflowY: 'initial' }}
      onHide={() => {
        setSelectedCountry(undefined);
        setSearchTerm('');
        onHide();
      }}
    >
      <DialogContentContainer>
        <LcaDialogHelpBox>
          <div>
            You can add LCA Practitioners to your project in two ways:
            <ul>
              <li>
                If you have already agreed to work with a practitioner from the list below, click <b>"Add practitioner"</b>
                &nbsp; to assign them directly to your EPD.
              </li>
              <li>
                If you need to discuss collaboration first, select one or more practitioners and click{' '}
                <b>"Request collaboration"</b>&nbsp; to notify them of your interest in collaborating on LCA/EPD development.
                Once confirmed, use <b>"Add practitioner"</b>&nbsp; as described above.
              </li>
            </ul>
          </div>
        </LcaDialogHelpBox>
        <DialogContentRow>
          <CompilerSelectControlledComponent
            label="Country"
            name="lcaPractitionerCountry"
            options={countries}
            value={selectedCountry}
            onChanged={onChangeLcaPractitionerCountry}
            placeholder="Select"
            isClearable={true}
          />
        </DialogContentRow>
        <DialogContentRow>
          <WizardTextInputControlledComponent
            label="Organisation name or e-mail address"
            tooltipPosition={'right'}
            value={undefined}
            placeholder="Type here to search"
            onChange={handleSearchLcaPractitionerCompanies}
            onChanged={() => {}}
            name="nameOrEmail"
            maxLength={150}
          />
        </DialogContentRow>
        {companiesQuery.isPending && (
          <LoadingContainer>
            <ProgressSpinner style={{ width: '40px', height: '40px' }} aria-label="Loading LCA practitioners" />
          </LoadingContainer>
        )}
        <LcaCompaniesList
          selectedPractitioners={selectedPractitioners}
          companyPractitioners={companyPractitioners}
          companies={filteredLcaPractitionerCompanies}
          selectedCompany={selectedLcaPractitionerCompany}
          onSelectCompany={setSelectedLcaPractitionerCompany}
          onRequestCollaboration={handleRequestCollaboration}
          onAddPractitioner={handleAddLcaPractitioner}
          buttonsAreDisabled={areButtonsDisabled}
        />
      </DialogContentContainer>
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)`
  width: 35rem;

  && .p-dialog-footer {
    padding-bottom: 30px;
    height: auto;
  }
`;

const DialogContentContainer = styled.div`
  padding: 0.25rem 2rem 2rem 2rem;
  scrollbar-gutter: stable;
`;

const DialogContentRow = styled.div`
  margin-top: 25px;
  margin-bottom: 8px;
`;

const DialogFooter = styled.div`
  padding-left: 1rem;
  padding-right: 1rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0;
`;

const InviteExternalPractitionerWrapper = styled.div`
  margin-top: 8px;
`;

const LcaDialogHelpBox = styled(HelpBox)`
  ul {
    margin: 0;
    padding-left: 30px;
  }
`;

export default LcaPractitionerSelectInputRendererDialog;
